/**
 * The performance year an objective belongs to (HR UAT list #27).
 *
 * Objectives have no year field: their dates place them in a year, and the lists filter on
 * those dates. An objective added for a year runs from 1 January to 31 December unless its
 * dates say otherwise, and its dates stay inside that year (the backend refuses others:
 * performance-service app/utils/objective_years.py).
 *
 * Dates are read and written as calendar days ('YYYY-MM-DD'), never through `new Date()`, so
 * 1 January can't slip into the year before on a UTC clock.
 */

type Dated = {
  start_date?: string | null
  end_date?: string | null
  startDate?: string | null
  endDate?: string | null
}

const yearOf = (value?: string | null): number | null => {
  const match = /^(\d{4})-/.exec(value ?? '')
  return match ? Number(match[1]) : null
}

/** The year an objective was added for: its start date's year, else its end date's. */
export function objectiveYear(objective: Dated): number | null {
  return yearOf(objective.start_date ?? objective.startDate) ?? yearOf(objective.end_date ?? objective.endDate)
}

/** An objective's dates for `year`: kept when they fall inside it, otherwise the whole year. */
export function datesForYear(
  year: number,
  startDate?: string | null,
  endDate?: string | null,
): { startDate: string; endDate: string } {
  const inYear = (value?: string | null) => yearOf(value) === year
  const start = inYear(startDate) ? startDate!.slice(0, 10) : `${year}-01-01`
  const end = inYear(endDate) ? endDate!.slice(0, 10) : `${year}-12-31`
  return start <= end ? { startDate: start, endDate: end } : { startDate: `${year}-01-01`, endDate: `${year}-12-31` }
}

/** How many of these objectives have a date outside `year` (a template's own dates, say):
 *  datesForYear moves those dates into the year, and the person adding them is told. */
export function countDatedOutside(year: number, objectives: Dated[]): number {
  return objectives.filter(o => {
    const start = o.start_date ?? o.startDate
    const end = o.end_date ?? o.endDate
    return (!!start && yearOf(start) !== year) || (!!end && yearOf(end) !== year)
  }).length
}

/** The notice for countDatedOutside, or '' when no date moved. */
export function datesMovedNotice(year: number, moved: number): string {
  if (moved === 0) return ''
  return ` ${moved === 1 ? 'One had' : `${moved} had`} dates outside ${year}; ${moved === 1 ? 'its dates were' : 'their dates were'} moved into ${year}.`
}
